import { motion, useMotionValue, useSpring, useTransform, useScroll } from "framer-motion";
import { MessageCircle, ArrowDown, Backpack, NotebookPen, Gift, Sparkles } from "lucide-react";
import { waGeneral, WA_DISPLAY } from "@/lib/whatsapp";
import { scrollToId } from "@/components/Navbar";

const HERO_IMG = "https://images.unsplash.com/photo-1779684998897-ce5de594a5ac?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA3MDR8MHwxfHNlYXJjaHw0fHxjb2xvcmZ1bCUyMHN0YXRpb25lcnklMjBkZXNrJTIwc3VwcGxpZXN8ZW58MHx8fHwxNzkxNjQ2MjEzfDA&ixlib=rb-4.1.0&q=85";

const LINES = [
  { text: "Papelería con", color: "text-ink" },
  { text: "chispa,", color: "text-kpink" },
  { text: "novedades que", color: "text-ink" },
  { text: "enamoran", color: "text-kcyan" },
];

const BADGES = [
  { icon: Backpack, label: "Útiles Escolares Top", className: "bg-kcyan", tilt: "-6deg", pos: "top-6 -left-4 lg:-left-10" },
  { icon: NotebookPen, label: "Agendas & Planners", className: "bg-kpink text-white", tilt: "4deg", pos: "top-1/3 -right-3 lg:-right-8" },
  { icon: Gift, label: "Regalos & Novedades", className: "bg-kpurple text-white", tilt: "-3deg", pos: "bottom-24 -left-3 lg:-left-8" },
  { icon: Sparkles, label: "Papelería Creativa", className: "bg-kyellow", tilt: "5deg", pos: "bottom-4 -right-4 lg:-right-10" },
];

const STATS = [
  { value: "100%", label: "Garantía de Calidad" },
  { value: "50%", label: "Seña para Pedidos" },
  { value: "24h", label: "Atención WhatsApp" },
  { value: "Py", label: "Envíos Nacionales" },
];

export default function Hero() {
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rotateX = useSpring(useTransform(my, [-0.5, 0.5], [7, -7]), { stiffness: 150, damping: 20 });
  const rotateY = useSpring(useTransform(mx, [-0.5, 0.5], [-9, 9]), { stiffness: 150, damping: 20 });
  const { scrollYProgress } = useScroll();
  const yParallax = useTransform(scrollYProgress, [0, 0.4], [0, -70]);

  const onMove = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width - 0.5);
    my.set((e.clientY - r.top) / r.height - 0.5);
  };

  return (
    <section id="inicio" data-testid="hero-section"
      className="relative pt-[72px] min-h-screen flex items-center overflow-hidden grain bg-paper">
      <motion.div style={{ y: yParallax }} className="absolute -top-24 -right-24 w-[420px] h-[420px] rounded-full bg-kyellow/50 blur-3xl pointer-events-none" />
      <div className="absolute top-1/2 -left-32 w-[380px] h-[380px] rounded-full bg-kpink/20 blur-3xl pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 py-16 lg:py-24 grid lg:grid-cols-2 gap-14 items-center w-full">
        <div>
          <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 border-2 border-ink bg-white rounded-full px-4 py-1.5 shadow-neo-sm mb-7">
            <span className="h-2 w-2 rounded-full bg-kgreen animate-pulse" />
            <span className="font-mono text-xs uppercase tracking-[0.2em] font-bold">Librería & Novedades · Luque</span>
          </motion.div>

          <h1 className="font-display font-extrabold tracking-tight leading-[1.02] text-4xl sm:text-5xl lg:text-6xl">
            {LINES.map((l, i) => (
              <span key={i} className="block overflow-hidden pb-1">
                <motion.span
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ delay: 0.15 + i * 0.13, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                  className={`block ${l.color}`}>
                  {l.text}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.8, duration: 0.6 }}
            className="mt-6 text-base sm:text-lg text-ink/70 max-w-md font-medium leading-relaxed">
            Útiles escolares, agendas, regalos y papelería creativa en Luque.
            Pedí lo que necesitás por WhatsApp y lo preparamos para vos.
          </motion.p>

          <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1, duration: 0.6 }}
            className="mt-8 flex flex-wrap gap-3">
            <a href="#catalogo" onClick={scrollToId("#catalogo")} data-testid="hero-catalog-cta"
              className="h-13 px-7 py-3.5 inline-flex items-center gap-2 rounded-full border-2 border-ink bg-kyellow font-display font-bold text-lg shadow-neo hover:shadow-neo-lg hover:-translate-y-1 transition-all duration-200">
              Explorar Catálogo <ArrowDown size={20} strokeWidth={2.5} />
            </a>
            <a href={waGeneral()} target="_blank" rel="noopener noreferrer" data-testid="hero-whatsapp-cta"
              className="h-13 px-7 py-3.5 inline-flex items-center gap-2 rounded-full border-2 border-ink bg-[#25D366] text-white font-display font-bold text-lg shadow-neo hover:shadow-neo-lg hover:-translate-y-1 transition-all duration-200">
              <MessageCircle size={20} strokeWidth={2.5} /> Pedir por WhatsApp
            </a>
          </motion.div>

          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.25, duration: 0.7 }}
            className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-lg">
            {STATS.map((s) => (
              <div key={s.label} className="border-2 border-ink bg-white rounded-2xl px-3 py-2.5 shadow-neo-sm">
                <div className="font-display font-bold text-xl leading-none">{s.value}</div>
                <div className="text-[11px] font-semibold text-ink/60 mt-1 leading-tight">{s.label}</div>
              </div>
            ))}
          </motion.div>
        </div>

        <motion.div initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.5, duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="relative" style={{ perspective: 900 }} onMouseMove={onMove} onMouseLeave={() => { mx.set(0); my.set(0); }}>
          <motion.div style={{ rotateX, rotateY }} className="relative">
            <div className="absolute inset-0 translate-x-3 translate-y-3 rounded-[2rem] bg-kpink border-2 border-ink" />
            <img src={HERO_IMG} alt="Productos de librería coloridos en Kuki Impresiones"
              className="relative w-full aspect-[4/5] sm:aspect-[5/5] object-cover rounded-[2rem] border-2 border-ink" />
            <div className="absolute -top-4 left-8 bg-kyellow border-2 border-ink rounded-full px-4 py-1.5 font-mono text-xs font-bold uppercase tracking-[0.15em] shadow-neo-sm -rotate-3">
              Nuevo en tienda
            </div>
          </motion.div>
          {BADGES.map((b, i) => (
            <motion.div key={b.label}
              initial={{ opacity: 0, scale: 0.6 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 1 + i * 0.12, type: "spring", stiffness: 200, damping: 14 }}
              className={`absolute ${b.pos}`}>
              <div className={`flex items-center gap-2 border-2 border-ink rounded-full pl-2.5 pr-4 py-2 font-bold text-xs sm:text-sm shadow-neo-sm animate-floaty ${b.className}`}
                style={{ "--tilt": b.tilt, animationDelay: `${i * 0.7}s` }}>
                <b.icon size={16} strokeWidth={2.5} />
                {b.label}
              </div>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
